import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import {
  Modal,
  Input,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  StatusIndicator,
} from '@unified-checkout/design-system';
import { Check, Xmark } from '@unified-checkout/design-system/icons/index';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { THREE_SECONDS } from '@app/dashboard/constants/shared';
import { MODAL_STEP_STATUS } from '@app/dashboard/constants/orders';
import { createBranch, type CreateBranchBody } from '@app/dashboard/services/inner_layout/branches';
import { CITIES } from '@app/dashboard/constants/terminal_management/branches';
import { getCountryFromDomain } from '@app/dashboard/utils/getCountryFromDomain';
import { useLangStore } from '@app/dashboard/store/lang';
import { numbersOnly } from '@app/dashboard/utils/formValidations';
import type { AddBranchModalProps } from '@app/dashboard/types/innerLayout/branches';
import styles from './add-branch-modal.module.scss';

const FIELD_NAMES = {
  BRANCH_NAME: 'branch_name',
  BRANCH_ID: 'branch_id',
  CITY: 'city',
  ADDRESS: 'address',
} as const;

export const AddBranchModal = ({ open, onClose, isMobile = false }: AddBranchModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [branchName, setBranchName] = useState('');
  const [branchId, setBranchId] = useState('');
  const [branchIdError, setBranchIdError] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [modalStep, setModalStep] = useState<{
    type: string;
    message: string;
  }>({
    type: MODAL_STEP_STATUS.START,
    message: '',
  });

  const { lang } = useLangStore();
  const country = getCountryFromDomain();
  const countryCities = CITIES[country as keyof typeof CITIES] ?? CITIES.EGY;
  const cityDisplayText =
    (countryCities ?? []).find((c) => c.en === city)?.[lang] ?? city;

  const resetForm = () => {
    setBranchName('');
    setBranchId('');
    setBranchIdError('');
    setCity('');
    setAddress('');
  };

  const handleClose = () => {
    resetForm();
    setModalStep({ type: MODAL_STEP_STATUS.START, message: '' });
    onClose();
  };

  const handleGoBackToForm = () => {
    setModalStep({ type: MODAL_STEP_STATUS.START, message: '' });
  };

  const handleModalStatus = ({ message, type }: { message: string; type: string }) => {
    setModalStep({ type, message });

    setTimeout(() => {
      if (type === MODAL_STEP_STATUS.SUCCESS) {
        setModalStep({ type: MODAL_STEP_STATUS.START, message: '' });
        handleClose();
      }
    }, THREE_SECONDS);
  };

  const { isPending, mutate } = useMutation({
    mutationKey: [QUERY_KEYS.CREATE_BRANCH],
    mutationFn: (body: CreateBranchBody) => createBranch(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_BRANCHES] });
      handleModalStatus({
        type: MODAL_STEP_STATUS.SUCCESS,
        message: t('innerLayout.branchesPage.modal.successMessage'),
      });
    },
    onError: (err: AxiosError<{ message?: string }>) => {
      const errorMessage = err?.response?.data?.message;
      const msg =
        errorMessage === 'duplicate'
          ? t('innerLayout.branchesPage.modal.duplicateError')
          : errorMessage ?? t('errors.somethingWentWrong');
      handleModalStatus({
        type: MODAL_STEP_STATUS.FAILED,
        message: msg,
      });
    },
  });

  const validateBranchId = (value: string) => {
    if (value.trim() === '') {
      setBranchIdError('');
      return;
    }
    if (!numbersOnly.pattern.test(value)) {
      setBranchIdError(numbersOnly.error_msg);
    } else {
      setBranchIdError('');
    }
  };

  const handleBranchIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBranchId(value);
    validateBranchId(value);
  };

  const handleSubmit = () => {
    mutate({
      branch_id: branchId.trim(),
      branch_name: branchName.trim(),
      city: city.trim(),
      address: address.trim(),
    });
  };

  const formValid =
    branchName.trim() !== '' &&
    branchId.trim() !== '' &&
    branchIdError === '' &&
    city.trim() !== '' &&
    address.trim() !== '';

  const addBranchModalBtnProps = [
    {
      size: 'lg' as const,
      color: 'primary' as const,
      variant: 'contained' as const,
      text: t('innerLayout.branchesPage.modal.addButton'),
      loading: isPending,
      disabled: !formValid || isPending,
      onClickHandler: handleSubmit,
    },
    {
      size: 'lg' as const,
      color: 'secondary' as const,
      variant: 'outlined' as const,
      text: t('form.cancel'),
      onClickHandler: handleClose,
    },
  ];

  const errorModalBtnProps = [
    {
      size: 'lg' as const,
      color: 'primary' as const,
      variant: 'contained' as const,
      text: t('innerLayout.branchesPage.modal.tryAgain'),
      onClickHandler: handleGoBackToForm,
    },
    {
      size: 'lg' as const,
      color: 'secondary' as const,
      variant: 'outlined' as const,
      text: t('form.cancel'),
      onClickHandler: handleClose,
    },
  ];

  const renderModalContent = () => {
    if (modalStep.type === MODAL_STEP_STATUS.START) {
      return (
        <div className={styles['add-branch-modal__body']}>
          <Input
            id={FIELD_NAMES.BRANCH_NAME}
            name={FIELD_NAMES.BRANCH_NAME}
            label={t('innerLayout.branchesPage.modal.branchName')}
            value={branchName}
            onChange={(e) => setBranchName(e.target.value)}
            placeholder={t('innerLayout.branchesPage.modal.branchNamePlaceholder')}
          />
          <Input
            id={FIELD_NAMES.BRANCH_ID}
            name={FIELD_NAMES.BRANCH_ID}
            label={t('innerLayout.branchesPage.modal.branchId')}
            value={branchId}
            onChange={handleBranchIdChange}
            placeholder={t('innerLayout.branchesPage.modal.branchIdPlaceholder')}
            error={branchIdError}
            status={branchIdError ? 'error' : 'default'}
          />
          <Dropdown label={t('innerLayout.branchesPage.modal.city')}>
            <DropdownButton
              type="input"
              placeholder={t('innerLayout.branchesPage.modal.cityPlaceholder')}
              text={cityDisplayText}
            />
            <DropdownMenu maxHeight="12rem">
              {(countryCities ?? []).map((c) => (
                <DropdownMenuItem
                  key={c.en}
                  text={c[lang]}
                  selected={city === c.en}
                  onClickHandler={() => setCity(c.en)}
                />
              ))}
            </DropdownMenu>
          </Dropdown>
          <Input
            id={FIELD_NAMES.ADDRESS}
            name={FIELD_NAMES.ADDRESS}
            label={t('innerLayout.branchesPage.modal.address')}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={t('innerLayout.branchesPage.modal.addressPlaceholder')}
          />
        </div>
      );
    } else {
      const isSuccess = modalStep.type === MODAL_STEP_STATUS.SUCCESS;
      const isFailed = modalStep.type === MODAL_STEP_STATUS.FAILED;

      const title = isSuccess
        ? t('innerLayout.branchesPage.modal.successTitle')
        : isFailed
        ? t('innerLayout.branchesPage.modal.errorTitle')
        : undefined;

      const description = isSuccess ? undefined : modalStep.message;

      const icon = isSuccess ? Check : Xmark;
      const status = isSuccess ? 'success' : 'error';

      return (
        <div className={styles['add-branch-modal__content']}>
          <StatusIndicator
            status={status}
            icon={icon}
            text={title}
            hintText={description}
          />
        </div>
      );
    }
  };

  const isSuccess = modalStep.type === MODAL_STEP_STATUS.SUCCESS;
  const isFailed = modalStep.type === MODAL_STEP_STATUS.FAILED;

  return (
    <Modal
      state={isSuccess ? 'success' : isFailed ? 'danger' : 'default'}
      open={open}
      position='center'
      isMobile={isMobile}
      hasCloseIcon={modalStep.type !== MODAL_STEP_STATUS.START}
      onCloseHandler={handleClose}
      closeOnClickOutside={false}
      title={
        modalStep.type === MODAL_STEP_STATUS.START
          ? t('innerLayout.branchesPage.modal.title')
          : undefined
      }
      btnsProps={
        modalStep.type === MODAL_STEP_STATUS.START
          ? addBranchModalBtnProps
          : modalStep.type === MODAL_STEP_STATUS.FAILED
          ? errorModalBtnProps
          : undefined
      }
    >
      {renderModalContent()}
    </Modal>
  );
};
